import { memo } from 'react';
import {
  alpha,
  Badge,
  Chip,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import { NavLink } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from '@/shared/auth/useAuth';
import type { Role } from '@/shared/auth/roles';

export interface NavItemProps {
  readonly to: string;
  readonly label: string;
  readonly icon?: ReactNode;
  readonly requiresRole?: Role;
  /**
   * Forwarded to `NavLink`'s own `end` prop — require an exact match rather
   * than the default URL-prefix match. Needed only when `to` is a prefix of
   * a sibling nav item's route (see `NavItemConfig.end`'s doc comment).
   */
  readonly end?: boolean;
  /** When true, the item renders icon-only with a tooltip showing the label. */
  readonly collapsed?: boolean;
  /** Unread/new-item count shown as a badge. Omitted or 0 renders no badge. */
  readonly badgeContent?: number;
  /** aria-label for the badge. */
  readonly badgeLabel?: string;
}

/**
 * Sidebar navigation link with role-gated visibility.
 * Renders nothing when the active user lacks `requiresRole`.
 *
 * When `collapsed` is true the link shrinks to icon-only and the label
 * surfaces as a hover tooltip — used by `AppShell`'s mini-variant drawer.
 *
 * `badgeContent` renders differently depending on available space: expanded
 * (label visible) shows a trailing pill after the label; collapsed
 * (icon-only mini rail, no room for a pill) falls back to the corner-dot
 * `Badge` wrapping the icon.
 */
const NavItemComponent = ({
  to,
  label,
  icon,
  requiresRole,
  end,
  collapsed = false,
  badgeContent,
  badgeLabel,
}: NavItemProps): JSX.Element | null => {
  const { hasRole } = useAuth();

  if (requiresRole && !hasRole(requiresRole)) {
    return null;
  }

  const hasBadge = badgeContent !== undefined && badgeContent > 0;
  const badgeAriaLabel = badgeLabel ?? `${badgeContent ?? 0} new`;
  const badgeDisplay = hasBadge && badgeContent! > 99 ? '99+' : badgeContent;

  const displayIcon =
    icon && hasBadge && collapsed ? (
      <Badge
        color="error"
        badgeContent={badgeContent}
        max={99}
        overlap="circular"
        aria-label={badgeAriaLabel}
      >
        {icon}
      </Badge>
    ) : (
      icon
    );

  const button = (
    <ListItemButton
      component={NavLink}
      to={to}
      end={end}
      sx={{
        minHeight: 32,
        borderRadius: 1,
        justifyContent: collapsed ? 'center' : 'flex-start',
        px: collapsed ? 1.5 : 1.25,
        py: 0.875,
        color: 'text.secondary',
        // Matches the design source's active nav treatment — a light-blue
        // tint + blue ink, not the generic grey MUI `action.selected`.
        '&.active': {
          backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
          color: 'primary.main',
          fontWeight: 600,
        },
        '&.active .MuiListItemIcon-root': { color: 'primary.main' },
      }}
    >
      {displayIcon ? (
        <ListItemIcon
          sx={{
            minWidth: collapsed ? 0 : 36,
            mr: collapsed ? 0 : 1.125,
            justifyContent: 'center',
            color: 'inherit',
          }}
        >
          {displayIcon}
        </ListItemIcon>
      ) : null}
      {!collapsed ? <ListItemText primary={label} /> : null}
      {!collapsed && hasBadge ? (
        <Chip
          label={badgeDisplay}
          color="error"
          size="small"
          aria-label={badgeAriaLabel}
          sx={{ ml: 1, height: 18, fontSize: '0.65rem', fontWeight: 700 }}
        />
      ) : null}
    </ListItemButton>
  );

  // Tooltip only when collapsed — otherwise the label is already visible
  // and a tooltip is noisy.
  return collapsed ? (
    <Tooltip title={label} placement="right" arrow>
      {button}
    </Tooltip>
  ) : (
    button
  );
};

export const NavItem = memo(NavItemComponent);
